import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import AddressFields, { type AddressData } from '../components/AddressFields';
import FormInput, { FormSelect, FormCheckbox, FormTextarea } from '../../setup/components/FormInput';

interface SkipTraceFormProps {
  brand: BrandConfig;
}

const SkipTraceForm: React.FC<SkipTraceFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const {
    typeData,
    documents,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as {
    subjectName?: string;
    subjectGender?: string;
    subjectDOB?: string;
    lastKnownAddresses?: AddressData[];
    addressNotAvailable?: boolean;
    specialInstructions?: string;
    isLastKnownAddressRequired?: boolean;
  };

  const [addresses, setAddresses] = useState<AddressData[]>(
    data.lastKnownAddresses ?? [{ street1: '', street2: '', city: '', state: 'CA', zip: '' }],
  );

  const updateAddresses = (newAddresses: AddressData[]) => {
    setAddresses(newAddresses);
    updateTypeData({ lastKnownAddresses: newAddresses } as Partial<typeof typeData>);
  };

  return (
    <div className="flex gap-6">
      <div className="flex-1 space-y-6">
        {/* Subject info */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Subject Information
          </h3>
          <FormInput
            brand={brand}
            label="Subject Name"
            name="subjectName"
            value={data.subjectName ?? ''}
            onChange={(e) => updateTypeData({ subjectName: e.target.value } as Partial<typeof typeData>)}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <FormSelect
              brand={brand}
              label="Gender"
              name="subjectGender"
              value={data.subjectGender ?? ''}
              onChange={(e) => updateTypeData({ subjectGender: e.target.value } as Partial<typeof typeData>)}
              options={[
                { value: '', label: 'Select...' },
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ]}
            />
            <FormInput
              brand={brand}
              label="Date of Birth"
              name="subjectDOB"
              type="date"
              value={data.subjectDOB ?? ''}
              onChange={(e) => updateTypeData({ subjectDOB: e.target.value } as Partial<typeof typeData>)}
            />
          </div>
        </div>

        {/* Addresses */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Last Known Addresses
          </h3>
          <FormCheckbox
            brand={brand}
            label="Address not available"
            name="addressNotAvailable"
            checked={data.addressNotAvailable ?? false}
            onChange={(e) => updateTypeData({ addressNotAvailable: e.target.checked } as Partial<typeof typeData>)}
          />
          {!data.addressNotAvailable && (
            <>
              {addresses.map((addr, i) => (
                <div key={`addr-${addr.street1}-${addr.city}-${addr.zip}`} className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs font-semibold" style={{ color: brand.colors.textLight }}>
                      Address {i + 1}
                    </p>
                    {addresses.length > 1 && (
                      <button
                        onClick={() => updateAddresses(addresses.filter((_, idx) => idx !== i))}
                        className="text-xs text-red-500"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <AddressFields
                    brand={brand}
                    prefix={`skipAddr-${i}`}
                    value={addr}
                    onChange={(updated) => {
                      const newAddrs = [...addresses];
                      newAddrs[i] = updated;
                      updateAddresses(newAddrs);
                    }}
                  />
                </div>
              ))}
              <button
                onClick={() => updateAddresses([...addresses, { street1: '', street2: '', city: '', state: 'CA', zip: '' }])}
                className="text-sm font-medium"
                style={{ color: brand.colors.link }}
              >
                + Add Address
              </button>
            </>
          )}
        </div>

        {/* Special instructions */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Special Instructions
          </h3>
          <FormTextarea
            brand={brand}
            label="Instructions"
            name="specialInstructions"
            value={data.specialInstructions ?? ''}
            onChange={(e) => updateTypeData({ specialInstructions: e.target.value } as Partial<typeof typeData>)}
            rows={4}
          />
        </div>

        {/* Documents */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Documents
          </h3>
          <DocumentUploader
            brand={brand}
            documents={documents}
            onUpload={uploadDocument}
            onRemove={removeDocument}
          />
        </div>
      </div>

      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel
          brand={brand}
          onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch { /* Error displayed in panel */ }
          }}
        />
      </div>
    </div>
  );
};

export default SkipTraceForm;
